import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkles, Quote, Zap, Wand2, RefreshCw, Shield, TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PublicHeader } from '../components/PublicHeader';
import Footer from '../components/Footer';

/* ─── Helpers ─────────────────────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const Eyebrow = ({ children, tone = 'indigo' }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    amber: 'bg-amber-50 text-amber-700 ring-amber-100',
    rose: 'bg-rose-50 text-rose-700 ring-rose-100',
    violet: 'bg-violet-50 text-violet-700 ring-violet-100',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ring-1 text-[11px] font-bold tracking-widest uppercase ${tones[tone]}`}>
      <Sparkles className="w-3 h-3" /> {children}
    </span>
  );
};

/* ─── Data ────────────────────────────────────────────────────────────── */
const VALUES = [
  {
    Icon: Wand2,
    title: 'Simplicity',
    body: 'A 12-year-old should be able to build a form. We obsess over removing every unnecessary click, label, and step — not piling them on.',
    accent: 'from-indigo-500 to-violet-500',
    tint: 'bg-indigo-50 text-indigo-700',
  },
  {
    Icon: Zap,
    title: 'Speed',
    body: "Forms must load fast, respond fast, and ship to production fast. If it takes more than 60 seconds to launch a working form, we've failed.",
    accent: 'from-amber-500 to-orange-500',
    tint: 'bg-amber-50 text-amber-700',
  },
  {
    Icon: RefreshCw,
    title: 'Automation',
    body: "Once a form is submitted, the next step shouldn't be manual. We give every form light-touch automation — notifications, syncs, and webhooks — so the work continues without you.",
    accent: 'from-emerald-500 to-teal-500',
    tint: 'bg-emerald-50 text-emerald-700',
  },
  {
    Icon: Shield,
    title: 'Reliability',
    body: '99.9% uptime is not marketing — it is the floor. Encrypted in transit, encrypted at rest, audit-logged, and resilient by design.',
    accent: 'from-cyan-500 to-blue-500',
    tint: 'bg-cyan-50 text-cyan-700',
  },
  {
    Icon: TrendingUp,
    title: 'Scalability',
    body: 'From the freelancer with 50 monthly submissions to the enterprise with 500,000, the same product scales — no rebuilds, no migrations.',
    accent: 'from-rose-500 to-fuchsia-500',
    tint: 'bg-rose-50 text-rose-700',
  },
];

const TIMELINE = [
  {
    year: '2024',
    label: 'Q2',
    title: 'The frustration',
    body: 'Co-founders shipped the same internal "intake form" project for the 5th client and decided forms shouldn\'t be this painful.',
  },
  {
    year: '2024',
    label: 'Q4',
    title: 'First prototype',
    body: '6 weeks of weekend hacking. 12 fields. 1 conditional rule. One submission ever. It worked.',
  },
  {
    year: '2025',
    label: 'Q1',
    title: 'Public beta',
    body: 'Opened the gates with a handful of early-access teams. Shipped Webhook integrations, signatures, and analytics in 90 days.',
  },
  {
    year: '2025',
    label: 'Q3',
    title: 'First paying customers',
    body: 'Onboarded our first cohort of paying customers across multiple countries. Added approval workflows and Razorpay subscriptions.',
  },
  {
    year: '2026',
    label: 'Q1',
    title: 'Where we are today',
    body: 'Trusted by growing teams in 5 countries to build smarter forms — with light post-submission automation built in. Shipping AI-assisted form generation and quotation flows next.',
    highlight: true,
  },
];

const STATS = [
  { value: '10K+',  label: 'Submissions processed' },
  { value: '5',     label: 'Countries served' },
  { value: '99.9%', label: 'Platform uptime' },
  { value: '<2h',   label: 'Avg. support reply' },
];

/* ─── Component ───────────────────────────────────────────────────────── */
export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <PublicHeader />

      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <section
        className="relative pt-16 lg:pt-24 pb-16 lg:pb-20 overflow-hidden bg-gradient-to-b from-indigo-50/50 via-white to-white"
        data-testid="about-hero"
      >
        <div aria-hidden className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-indigo-100/70 blur-3xl pointer-events-none" />
        <div aria-hidden className="absolute -bottom-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-violet-100/60 blur-3xl pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 relative text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <Eyebrow tone="indigo">About Formlio</Eyebrow>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.05 }}
            className="mt-5 text-4xl sm:text-5xl lg:text-[3.6rem] font-bold text-slate-900 tracking-tight leading-[1.05] max-w-4xl mx-auto"
            data-testid="about-hero-heading"
          >
            We're building the{' '}
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              smartest form builder
            </span>{' '}
            for modern teams.
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.12 }}
            className="mt-6 text-base sm:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto"
          >
            Formlio is an advanced form builder with built-in post-submission automation —
            so every form you create doesn't just collect data, it can also trigger the next action automatically.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.2 }}
            className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <Button asChild size="lg" className="rounded-full h-12 px-7 bg-indigo-600 hover:bg-[#F59E0B] text-white shadow-lg shadow-indigo-500/25 transition-all">
              <Link to="/signup" className="inline-flex items-center gap-2" data-testid="about-hero-cta">
                Start Free <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 border-slate-300 hover:border-indigo-300 hover:bg-indigo-50">
              <Link to="/contact" data-testid="about-hero-talk-founder">Talk to founder</Link>
            </Button>
          </motion.div>

          {/* Quick stats strip */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            transition={{ delay: 0.3 }}
            className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto"
            data-testid="about-stats-strip"
          >
            {STATS.map((s) => (
              <div key={s.label} className="bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl px-4 py-4 shadow-sm">
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{s.value}</p>
                <p className="text-xs text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── STORY ────────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="about-story">
        <div className="max-w-4xl mx-auto px-6">
          {/* Centered header */}
          <div className="text-center mb-12">
            <Eyebrow tone="violet">Our Story</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              Why Formlio{' '}
              <span className="bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">exists</span>.
            </h2>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
            className="space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            <p>
              Every team we worked with had the same problem: <strong className="text-slate-900">building a form was easy — everything that should happen after was hard.</strong>{' '}
              Lead forms dumped to spreadsheets nobody refreshed. Signed PDFs lived in inboxes nobody searched.
              Approvals stalled in Slack threads nobody read.
            </p>
            <p>
              So we built Formlio as an <strong className="text-slate-900">advanced form builder</strong> — drag-and-drop, conditional logic,
              signatures, file uploads, custom branding, the works. Then we added a layer of <strong className="text-slate-900">post-submission automation</strong>:
              every form can route, notify, sync to Google Sheets, send a webhook, or fire a follow-up email — without you writing a line of code.
            </p>
            <p>
              Today we help <strong className="text-slate-900">customer success teams</strong> capture leads faster,{' '}
              <strong className="text-slate-900">HR teams</strong> collect signed offer letters in minutes, and{' '}
              <strong className="text-slate-900">SMBs</strong> onboard customers with forms that actually feel like a product.
            </p>

            {/* Founder quote */}
            <div className="mt-10 relative pl-6 border-l-4 border-indigo-200 max-w-2xl mx-auto">
              <Quote aria-hidden className="absolute -left-4 -top-2 w-7 h-7 text-indigo-300/80 bg-white" />
              <p className="text-slate-800 italic leading-relaxed">
                "Every business needs forms. We just made sure yours look beautiful, work intelligently, and don't end at &lsquo;Submit&rsquo;."
              </p>
              <p className="mt-3 text-sm font-semibold text-slate-700">
                Hetal Vansh <span className="text-slate-400 font-normal">— Founder & CEO</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── MISSION ──────────────────────────────────────────────── */}
      <section
        className="relative py-20 lg:py-28 overflow-hidden bg-gradient-to-br from-indigo-900 via-violet-900 to-slate-900 text-white"
        data-testid="about-mission"
      >
        <div aria-hidden className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }} />

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          className="relative max-w-5xl mx-auto px-6 text-center"
        >
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-[11px] font-bold tracking-widest uppercase ring-1 ring-white/15 backdrop-blur">
            <Sparkles className="w-3 h-3" /> Our Mission
          </span>

          <h2 className="mt-6 text-3xl sm:text-4xl lg:text-[3.2rem] font-bold tracking-tight leading-[1.1]">
            Make every form a{' '}
            <span className="bg-gradient-to-r from-amber-300 via-orange-300 to-rose-300 bg-clip-text text-transparent">
              powerful, intelligent product
            </span>
            .
          </h2>

          <p className="mt-8 text-base sm:text-lg text-indigo-100/90 leading-relaxed max-w-2xl mx-auto">
            We believe forms shouldn't be the dumbest tool in your stack. Formlio gives you the design control of a
            premium product, the logic of a power tool, and just enough automation to turn each submission into the
            next business action.
          </p>
        </motion.div>
      </section>

      {/* ─── VALUES ───────────────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Eyebrow tone="amber">Our Values</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              Five things we never compromise on.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600">
              These aren't slogans — they decide every feature we ship and every one we say no to.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.Icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ delay: i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6 }}
                  className="group relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-xl hover:border-slate-300 transition-all"
                  data-testid={`value-card-${v.title.toLowerCase()}`}
                >
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${v.accent} text-white flex items-center justify-center shadow-md mb-4`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.body}</p>
                  <span className={`absolute top-5 right-5 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${v.tint}`}>
                    0{i + 1}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── TIMELINE / JOURNEY ───────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white" data-testid="about-timeline">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Eyebrow tone="rose">Product Journey</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
              From a weekend hack to a{' '}
              <span className="bg-gradient-to-r from-rose-600 to-orange-600 bg-clip-text text-transparent">smart form builder</span>
              .
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600">
              From idea to customers across 5 countries.
            </p>
          </div>

          <div className="relative">
            {/* Vertical spine */}
            <div aria-hidden className="absolute left-[1.05rem] sm:left-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-indigo-200 via-violet-200 to-rose-200 sm:-translate-x-1/2" />

            <div className="space-y-12">
              {TIMELINE.map((t, i) => {
                const onLeft = i % 2 === 0;
                return (
                  <motion.div
                    key={`${t.year}-${t.label}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className="relative grid grid-cols-1 sm:grid-cols-2 sm:gap-12 items-center"
                    data-testid={`timeline-item-${i}`}
                  >
                    {/* Dot on spine */}
                    <span
                      className={`absolute left-[1.05rem] sm:left-1/2 -translate-x-1/2 z-10 w-5 h-5 rounded-full ring-4 ring-white shadow-md ${
                        t.highlight
                          ? 'bg-gradient-to-br from-amber-400 to-orange-500 animate-pulse'
                          : 'bg-gradient-to-br from-indigo-500 to-violet-500'
                      }`}
                    />

                    {/* LEFT slot */}
                    <div className={`hidden sm:flex justify-end ${onLeft ? '' : 'invisible'}`}>
                      {onLeft && (
                        <div
                          className={`text-right rounded-2xl border p-6 shadow-sm hover:shadow-lg transition-all max-w-md ${
                            t.highlight
                              ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className={`inline-flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full mb-3 ${
                            t.highlight ? 'bg-amber-200 text-amber-900' : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {t.year} · {t.label}
                            {t.highlight && <span className="ml-1">★</span>}
                          </div>
                          <h3 className="text-lg font-bold text-slate-900 mb-1.5">{t.title}</h3>
                          <p className="text-sm text-slate-600 leading-relaxed">{t.body}</p>
                        </div>
                      )}
                    </div>

                    {/* RIGHT slot */}
                    <div className={`hidden sm:flex justify-start ${!onLeft ? '' : 'invisible'}`}>
                      {!onLeft && (
                        <div
                          className={`text-left rounded-2xl border p-6 shadow-sm hover:shadow-lg transition-all max-w-md ${
                            t.highlight
                              ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className={`inline-flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full mb-3 ${
                            t.highlight ? 'bg-amber-200 text-amber-900' : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {t.year} · {t.label}
                            {t.highlight && <span className="ml-1">★</span>}
                          </div>
                          <h3 className="text-lg font-bold text-slate-900 mb-1.5">{t.title}</h3>
                          <p className="text-sm text-slate-600 leading-relaxed">{t.body}</p>
                        </div>
                      )}
                    </div>

                    {/* MOBILE single card (always left-aligned next to spine) */}
                    <div className="sm:hidden pl-12">
                      <div
                        className={`rounded-2xl border p-6 shadow-sm ${
                          t.highlight
                            ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'
                            : 'bg-white border-slate-200'
                        }`}
                      >
                        <div className={`inline-flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full mb-3 ${
                          t.highlight ? 'bg-amber-200 text-amber-900' : 'bg-indigo-100 text-indigo-700'
                        }`}>
                          {t.year} · {t.label}
                          {t.highlight && <span className="ml-1">★</span>}
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-1.5">{t.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{t.body}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─────────────────────────────────────────────────── */}
      <section
        className="relative py-20 lg:py-24 bg-gradient-to-br from-indigo-600 to-violet-600 overflow-hidden"
        data-testid="about-final-cta"
      >
        <div aria-hidden className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto px-6 text-center relative"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-5">
            Build forms that{' '}
            <span className="bg-gradient-to-r from-amber-300 to-orange-200 bg-clip-text text-transparent">
              do more
            </span>
            .
          </h2>
          <p className="text-base sm:text-lg text-indigo-100 mb-8 max-w-xl mx-auto">
            Try every feature free for 14 days. No credit card. A real form builder, with real outcomes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-full h-14 px-8 text-base font-semibold bg-white hover:bg-[#F59E0B] text-indigo-700 hover:text-white shadow-2xl shadow-black/20 transition-all">
              <Link to="/signup" className="inline-flex items-center gap-2" data-testid="about-cta-start">
                Start Free <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full h-14 px-8 text-base font-semibold bg-transparent border-2 border-white/40 text-white hover:bg-white/10 hover:border-white transition-all">
              <Link to="/contact" data-testid="about-cta-contact">Talk to founder</Link>
            </Button>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
